import 'whatwg-fetch';
import cuid from 'cuid';
import urlParse from 'url-parse';
import BTCookies from '../common/cookies';

import {MessageHub} from './messagehub';
import {getUrlParams, yieldToMain} from '../common/util';
import {checkConsentRequirements, requestTrackingConsent} from './ccpa';
import {TOPIC_SPLIT_TEST_LOADED} from './messagehub/topics';

const SESSION_STORAGE_ATTRIBUTION_KEY = 'bt.sessionAttr';

export class BTTrackersManager {
  constructor({
    settings,
    trackers = {},
    pageTypePaths,
    env = 'staging',
    messageHub = new MessageHub(),
  }) {
    this.grp('Startup', () => {
      this.env = env;
      this.messageHub = messageHub;
      this.trackers = Object.keys(trackers).reduce((trackerMap, k) => {
        trackerMap[k] = new trackers[k]({messageHub});
        return trackerMap;
      }, {});
      this.pageTypePaths = pageTypePaths;
      this.pageType = Object.keys(pageTypePaths).find(x => pageTypePaths[x].test(window.location.pathname));
      this.config = settings;
      this.debug = this.config.debug;

      if (this.debug) {
        this.logWarn('Debug Mode is Enabled');
      }

      const {consentBar: {disabledPages: consentBarDisabledPages = []} = {} } = settings;

      const url = urlParse(window.location.href);
      this.disableTrackingConsentRequest = consentBarDisabledPages.some(page => url.pathname.includes(page));

      Object.values(this.trackers).forEach(x => {
        try {
          x.configure(this.config, this.pageType);
        } catch (err) {
          this.log(`Failed to configure tracker '${x.getName()}'`, err);
        }
      });
    });
  }

  set page(page) {
    this.allTrackers.filter(x => x.enabled).forEach(x => { x.page = page; });
  }

  get sessionUtms() {
    try {
      if (window.sessionStorage) {
        return JSON.parse(window.sessionStorage.getItem(SESSION_STORAGE_ATTRIBUTION_KEY) || '{}');
      }
    } catch (e) {
      console.error('BTTrackersManager.sessionUtms get failed', e);
    }
    return {};
  }

  set sessionUtms(val) {
    try {
      if (window.sessionStorage) {
        window.sessionStorage.setItem(SESSION_STORAGE_ATTRIBUTION_KEY, JSON.stringify(val));
      }
    } catch (e) {
      console.error('BTTrackersManager.sessionUtms set failed', e);
    }
  }

  /**
   * Gets all trackers managed by this manager instance.
   * @returns {BTTrackerBase[]}
   */
  get allTrackers() {
    return Object.values(this.trackers);
  }

  getUUID(uid, eventName) {
    return `${uid}-${eventName}`;
  }

  once(uuid, callback, params) {
    if (!this.isUnique(uuid)) {
      this.log('Skipping because uuid not unique', {uuid, params});
      return;
    }

    this.log('Uniquely calling', {uuid, params});
    callback(...params);

    this.setUnique(uuid);
  }

  onceServer(uuid, callback, params) {
    let timeoutId = null;
    Promise.race([
      new Promise(resolve => {
        timeoutId = setTimeout(() => {
          console.error('onceServer timed out', {
            uuid,
            params,
          });
          resolve(true);
        }, 5000);
      }),
      this.isUniqueServer(uuid),
    ]).then(isUnique => {
      clearTimeout(timeoutId);
      if (isUnique) {
        this.log('Uniquely calling', {uuid, params});
        callback(...params);
      } else {
        this.log('Skipping because uuid not unique', {uuid, params});
      }
    }).catch(e => {
      console.error('onceServer failed', e);
    });
  }

  event(type, params, ...restArgs) {
    params.eventId = params.eventId || cuid();
    params.eventTime = params.eventTime || Math.round(new Date().getTime() / 1000);
    this.grp('Event', () => {
      this.logDebug('Event', {type, params, args: restArgs});
      this.allTrackers
        .filter(x => x.enabled && (x.events == null || x.events.includes(type)))
        .forEach(x => x.event(type, params, ...restArgs));
    });
  }

  eventOnce(uid, ...params) {
    const uuid = this.getUUID(uid, 'event');
    this.once(uuid, this.event.bind(this), params);
  }

  view(options, ...rest) {
    const {
      utm_source: utmSource,
      utm_campaign: utmCampaign,
      utm_medium: utmMedium,
      utm_content: utmContent,
      utm_term: utmTerm,
    } = getUrlParams();

    const hasUtms = [
      utmSource,
      utmCampaign,
      utmMedium,
      utmContent,
      utmTerm,
    ].reduce((val, utm) => val || typeof utm !== 'undefined', false);

    if (hasUtms) {
      this.sessionUtms = {utmSource, utmCampaign, utmMedium, utmContent, utmTerm};
    }

    const optionsWithAttribution = {sessionAttribution: {...this.sessionUtms}, ...options};
    optionsWithAttribution.eventId = optionsWithAttribution.eventId || cuid();

    this.grp('View', () => {
      this.allTrackers.filter(x => x.enabled)
        .forEach(x => {
          try {
            x.view(optionsWithAttribution, ...rest);
          } catch (err) {
            console.error(`Error in tracker ${x.getName()} - view`, err);
            throw err;
          }
        });
    });
  }

  viewOnce(uid, ...params) {
    const uuid = this.getUUID(uid, 'view');
    this.once(uuid, this.view.bind(this), params);
  }

  action(type, action, category, options, ...rest) {
    let optionsObj = options || {};
    optionsObj.eventId = optionsObj.eventId || cuid();
    optionsObj.eventTime = optionsObj.eventTime || Math.round(new Date().getTime() / 1000);
    let actionParams = [type, action, category];
    if (typeof action === 'object') {
      optionsObj = action;
      actionParams = [type];
    }

    const optionsWithAttribution = {sessionAttribution: {...this.sessionUtms}, ...optionsObj};
    optionsWithAttribution.eventId = optionsWithAttribution.eventId || cuid();
    actionParams = [...actionParams, optionsWithAttribution, ...rest];

    this.grp('Action', () => {
      this.allTrackers
        .filter(x => x.enabled && (x.actions == null || x.actions.includes(type)))
        .forEach(x => {
          try {
            x.action(...actionParams);
          } catch (err) {
            console.error(`Error in tracker ${x.getName()} - action`, err);
            throw err;
          }
        });
    });
  }

  actionOnce(uid, ...params) {
    const uuid = this.getUUID(uid, 'action');
    this.once(uuid, this.action.bind(this), params);
  }

  purchase(order, items, options, ...rest) {
    const optionsWithAttribution = {sessionAttribution: {...this.sessionUtms}, ...options};
    optionsWithAttribution.eventId = optionsWithAttribution.eventId || cuid();

    this.grp('Purchase', () => {
      this.allTrackers.filter(x => x.enabled)
        .forEach(x => {
          try {
            x.purchase(order, items, optionsWithAttribution, ...rest);
          } catch (err) {
            console.error(`Error in tracker ${x.getName()} - purchase`, err);
          }
        });
    });
  }

  purchaseOnce(uid, order, items, options, ...params) {
    const uuid = this.getUUID(uid, 'purchase');

    if (options?.checkServerUUID) {
      order.uuid = uuid;
      this.onceServer(uuid, this.purchase.bind(this), [order, items, options, ...params]);
    } else {
      this.once(uuid, this.purchase.bind(this), [order, items, options, ...params]);
    }
  }

  /**
   * Initializes trackers and returns a function to be called when an opt in (or out) is received from the visitor,
   * which then initializes trackers that are CCPA aware.
   * @param {boolean} debug? - `true` if debug mode is enabled; otherwise, `false`.
   * @param {Function} onInitComplete? - A function to be called when Trackers initialization is complete
   *        (excluding CCPA Trackers).
   * @param {Object} restParams - Additional parameters to be passed to the `init()` function on each Tracker.
   * @returns Promise
   */
  async init({debug, onInitComplete, ...restParams} = {}) {
    if (debug) {
      this.debug = true;
    }

    const {splitTests: {vwo: vwoSplitTest = {} } = {} } = this.config || {};

    await this.grpAsync('Init', async () => {
      if (vwoSplitTest.enabled) {
        await new Promise(resolve => {
          const timeoutId = setTimeout(() => {
            // eslint-disable-next-line max-len
            this.log(`[VWO] TrackersJS TIMED OUT waiting ${vwoSplitTest.timeout} ms for the ${TOPIC_SPLIT_TEST_LOADED} message; continuing initialization`);
            resolve();
          }, vwoSplitTest.timeout);

          this.messageHub.subscribe({
            topic: TOPIC_SPLIT_TEST_LOADED,
            handler: () => {
              this.log(`[VWO] TrackersJS received ${TOPIC_SPLIT_TEST_LOADED} message; continuing initialization`);
              clearTimeout(timeoutId);
              resolve();
            },
          });
        });
      }

      const sortByOrderFn = (a, b) => {
        // eslint-disable-next-line eqeqeq
        if (a.order === b.order) {
          return 0;
        }
        if (a.order > b.order) {
          return 1;
        }
        return -1;
      };

      const trackerInitFn = async tracker => {
        try {
          await yieldToMain();
          this.logDebug(`Initializing ${tracker.getName()} ...`);
          tracker.init({trackersMap: this.trackers, ...restParams});
        } catch (err) {
          this.logError('Failed to initialize tracker', {error: err, tracker});
        }
      };

      // For trackers that don't require consent, we initialize them immediately.
      const nonCCPATrackers = Object.keys(this.trackers)
        .map(k => this.trackers[k])
        .filter(x => x.enabled && !x.requiresTrackingConsent)
        .sort(sortByOrderFn);

      this.logDebug('Initializing non CCPA trackers', nonCCPATrackers.map(t => t.getName()));
      nonCCPATrackers.forEach(trackerInitFn);

      if (typeof onInitComplete === 'function') {
        onInitComplete(this);
      }

      const {isConsentRequired, hasConsented, hasPreviouslyResponded} = await checkConsentRequirements(this.config);

      // These trackers require consent for some or all tracking and are initialized with the visitor's
      // preference to opt in or out of personalized tracking.
      const trackersRequiringConsent = Object.keys(this.trackers)
        .map(k => this.trackers[k])
        .filter(x => x.enabled && x.requiresTrackingConsent)
        .sort(sortByOrderFn);

      this.log('Initializing consent-based trackers', trackersRequiringConsent.map(t => t.getName()));
      this.logDebug('Consent Requirements', {isConsentRequired, hasConsented, hasPreviouslyResponded});

      trackersRequiringConsent.forEach(t => {
        t.isRestrictedDataProcessingEnabled = isConsentRequired && !hasConsented;
        trackerInitFn(t);
      });

      if (isConsentRequired && !hasConsented && !hasPreviouslyResponded && !this.disableTrackingConsentRequest) {
        const {consented} = await requestTrackingConsent();
        if (consented) {
          trackersRequiringConsent.forEach(t => {
            this.logDebug('Consent response received, initializing uninitialized consent-based trackers.', consented);
            t.isRestrictedDataProcessingEnabled = !consented;
            if (!t.initialized) {
              this.log('Initializing Tracker', {name: t.getName()});
              trackerInitFn(t);
            }
          });
        }
      }
    });
  }

  logDebug(message, params = {}) {
    return this.log(message, {__level: 'debug', ...params});
  }

  logWarn(message, params = {}) {
    return this.log(message, {__level: 'warn', ...params});
  }

  logError(message, params = {}) {
    return this.log(message, {__level: 'error', ...params});
  }

  log(message, {__level = 'log', ...params} = {}) {
    let logMethod = null;
    switch (__level) {
      case 'debug':
        if (!this.debug) {
          return;
        }
        logMethod = window.console && window.console.debug;
        break;
      case 'warn':
      case 'warning':
        logMethod = window.console && window.console.warn;
        break;
      case 'error':
        logMethod = window.console && window.console.error;
        break;
      default:
        logMethod = window.console && window.console.log;
        break;
    }
    if (logMethod) {
      if (typeof logMethod.bind === 'function') {
        logMethod = logMethod.bind(window.console);
      }
      logMethod(message, params);
    }
  }

  /**
   * Executes the given function within a log group.
   * @param name
   * @param func
   * @returns {*} - Returns whatever the wrapped function returns
   */
  grp(name, func) {
    window.console.group(name);
    const ret = func();
    window.console.groupEnd();

    // eslint-disable-next-line consistent-return
    return ret;
  }

  /**
   * Executes the given function within a log group.
   * @param name
   * @param {() -> Promise} func
   * @returns {*} - Returns whatever the wrapped function returns
   */
  async grpAsync(name, func) {
    window.console.group(name);
    const ret = await func();
    window.console.groupEnd();

    // eslint-disable-next-line consistent-return
    return ret;
  }

  isUnique(uid) {
    const obj = BTCookies.getJSON('uids');
    return !uid || !obj || !obj[uid];
  }

  setUnique(uid) {
    if (!uid) {
      return;
    }
    const obj = BTCookies.getJSON('uids') || {};
    obj[uid] = true;
    BTCookies.set('uids', obj);
  }

  async isUniqueServer(uuid) {
    if (!uuid) {
      return true; // match isUnique behavior
    }
    uuid = `${uuid}`.trim();

    const response = await fetch(
      `${this.config.statsUrl}session/known?uuid=${encodeURIComponent(uuid)}`,
      {headers: {Accept: 'application/json'} },
    );

    if (!response.ok) {
      console.error('isUniqueServer request failed', {status: response.status, headers: response.headers});
      const responseText = await response.text();
      console.error('isUniqueServer request failed', {content: responseText});
      return true;
    }

    const isKnown = await response.json();
    return !isKnown;
  }

  subscribe({topic, handler}) {
    this.messageHub.subscribe({topic, handler});
  }

  publish({topic, message} = {}) {
    if (topic && typeof message !== 'undefined') {
      this.messageHub.publish({topic, message});
    }
  }
}
